import {ThemeColorPath, ThemeColorTextPath} from 'platform/foundation';
import {formatPhoneNumber} from 'platform/locale';
import {match, Pattern} from 'ts-pattern';

import {isNotNil} from 'ramda';

import {TelephonyCall, TelephonyInboundCall, TelephonyOutboundCall} from '@dms/api/settings';
import i18n from '@dms/i18n';
import {formatCallDuration} from '@dms/shared';

import {Nullish} from 'shared/web';

export interface TelephonyCallIndicatorAppearance {
  label: string;
  dotColor: ThemeColorPath;
  backgroundColor: ThemeColorPath;
  borderColor: ThemeColorPath;
  textColor: ThemeColorTextPath;
}

const SEPARATOR = ' · ';

const BLUE = {
  dotColor: 'palettes.blue.60.100',
  backgroundColor: 'palettes.blue.10.100',
  borderColor: 'palettes.blue.30.100',
  textColor: 'link',
} as const;

const ORANGE = {
  dotColor: 'palettes.orange.60.100',
  backgroundColor: 'palettes.orange.10.100',
  borderColor: 'palettes.orange.30.100',
  textColor: 'warning',
} as const;

const GREEN = {
  dotColor: 'palettes.green.60.100',
  backgroundColor: 'palettes.green.10.100',
  borderColor: 'palettes.green.30.100',
  textColor: 'success',
} as const;

const GREY = {
  dotColor: 'palettes.neutral.100.100',
  backgroundColor: 'palettes.neutral.20.100',
  borderColor: 'palettes.neutral.40.100',
  textColor: 'secondary',
} as const;

const RED = {
  dotColor: 'palettes.red.60.100',
  backgroundColor: 'palettes.red.10.100',
  borderColor: 'palettes.red.30.100',
  textColor: 'danger',
} as const;

const getOutboundOutcomeLabel = (call: TelephonyOutboundCall) =>
  match(call)
    .with({outcome: 'connected'}, () => i18n.t('page.telephony.indicator.ended'))
    .with({outcome: 'customer_not_reached'}, () =>
      i18n.t('page.telephony.indicator.customerNotReached')
    )
    .with({outcome: 'not_confirmed'}, () => i18n.t('page.telephony.indicator.notConfirmed'))
    // For a browser call the "user leg" is the tab: it never reached Twilio.
    .with({outcome: 'user_not_reached', channel: 'browser'}, () =>
      i18n.t('page.telephony.indicator.browserNotConnected')
    )
    .with({outcome: 'user_not_reached', channel: 'mobile'}, () =>
      i18n.t('page.telephony.indicator.userNotReached')
    )
    .with({outcome: null}, () => i18n.t('page.telephony.indicator.ended'))
    .exhaustive();

const getOutboundEndedAppearance = (call: TelephonyOutboundCall) => {
  const isConnected = call.outcome === 'connected';
  const duration = isConnected ? formatCallDuration(call.durationSeconds ?? 0) : null;

  return {
    ...(isConnected ? GREY : RED),
    label: [getOutboundOutcomeLabel(call), duration].filter(Boolean).join(SEPARATOR),
  };
};

/**
 * An inbound call answered by a colleague ends at once for the rest of that ring step; only the
 * answerer gets the duration. `passed_on` is never shown: the indicator clears at once.
 */
const getInboundEndedAppearance = (call: TelephonyInboundCall, viewerId: string | Nullish) =>
  match(call.outcome)
    .with('answered', () =>
      isNotNil(call.answeredBy) && call.answeredBy.id !== viewerId
        ? {
            ...GREY,
            label: i18n.t('page.telephony.indicator.answeredBy', {name: call.answeredBy.name}),
          }
        : {
            ...GREY,
            label: [
              i18n.t('page.telephony.indicator.ended'),
              formatCallDuration(call.durationSeconds ?? 0),
            ].join(SEPARATOR),
          }
    )
    .with('nobody_answered', () => ({
      ...RED,
      label: i18n.t('page.settings.telephony.routingSummaryNoAnswer'),
    }))
    .with('caller_hung_up', () => ({
      ...RED,
      label: i18n.t('page.telephony.indicator.callerHungUp'),
    }))
    .with(Pattern.union('passed_on', null), () => ({
      ...GREY,
      label: i18n.t('page.telephony.indicator.ended'),
    }))
    .exhaustive();

/**
 * What the top-bar chip says and which colour family it wears for a call state. The customer is
 * named by `customerName`, else by the dialled (outbound) or calling (inbound) number, which an
 * inbound caller may withhold.
 */
export const getTelephonyCallIndicatorAppearance = (
  call: TelephonyCall,
  viewerId: string | Nullish,
  elapsedSeconds: number | Nullish
): TelephonyCallIndicatorAppearance => {
  const phoneNumber = isNotNil(call.customerPhoneNumber)
    ? formatPhoneNumber(call.customerPhoneNumber)
    : i18n.t('page.telephony.indicator.withheldNumber');
  const customer = call.customerName ?? phoneNumber;

  return match(call)
    .with({direction: 'outbound', status: 'ringing_user'}, () => ({
      ...BLUE,
      label: i18n.t('page.telephony.indicator.ringingUser'),
    }))
    .with({direction: 'outbound', status: 'waiting_for_confirmation'}, () => ({
      ...ORANGE,
      label: i18n.t('page.telephony.indicator.waitingForConfirmation'),
    }))
    .with({direction: 'outbound', status: 'connecting_customer'}, () => ({
      ...BLUE,
      label: i18n.t('page.telephony.indicator.connectingCustomer', {customer}),
    }))
    .with({direction: 'outbound', status: 'ended'}, getOutboundEndedAppearance)
    .with({direction: 'inbound', status: 'ringing'}, () => ({
      ...BLUE,
      label: [i18n.t('page.telephony.indicator.incomingCall'), customer].join(SEPARATOR),
    }))
    .with({direction: 'inbound', status: 'ended'}, (inboundCall) =>
      getInboundEndedAppearance(inboundCall, viewerId)
    )
    .with({status: 'in_call'}, () => ({
      ...GREEN,
      label: [customer, formatCallDuration(elapsedSeconds ?? 0)].join(SEPARATOR),
    }))
    .exhaustive();
};
